import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { emailNotificationService, type EmailConfig } from '../../utils/emailNotifications';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { MailIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
interface EmailNotificationsConfigProps {
  isReadOnly?: boolean;
}
export function EmailNotificationsConfig({
  isReadOnly = false
}: EmailNotificationsConfigProps) {
  const {
    showToast
  } = useToast();
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: false,
    senderName: 'Savidna CRM'
  });
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    try {
      const current = (emailNotificationService as any).getConfig?.();
      if (current) {
        setConfig({
          publicKey: current.publicKey || '',
          serviceId: current.serviceId || '',
          templateId: current.templateId || '',
          enabled: !!current.enabled,
          senderName: current.senderName || 'Savidna CRM'
        });
      }
    } catch (e) {
      logger.warn('No se pudo cargar configuración de email:', e);
    }
  }, []);
  const handleSave = async () => {
    if (isReadOnly) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const saver = (emailNotificationService as any).saveConfig || (emailNotificationService as any).updateConfig || (emailNotificationService as any).setConfig;
      if (typeof saver === 'function') {
        await saver.call(emailNotificationService, {
          ...config,
          enabled: true
        });
        showToast({
          type: 'success',
          title: 'Configuración guardada',
          message: 'La configuración de notificaciones fue guardada correctamente.'
        });
        setSaveMessage({
          type: 'success',
          text: 'Configuración guardada correctamente.'
        });
      } else {
        setSaveMessage({
          type: 'error',
          text: 'No se encontró el método para guardar la configuración.'
        });
      }
    } catch (err: any) {
      logger.error('Error guardando configuración de email:', err);
      setSaveMessage({
        type: 'error',
        text: err?.message || 'Error guardando la configuración.'
      });
    } finally {
      setIsSaving(false);
    }
  };
  return <Card data-id="element-3066">
      <CardHeader data-id="element-3067">
        <CardTitle className="flex items-center gap-2" data-id="element-3068">
          <MailIcon className="w-5 h-5 text-teal-500" data-id="element-3069" />
          Notificaciones por Email
        </CardTitle>
      </CardHeader>
      <CardContent data-id="element-3070">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-3071">
          Configura las credenciales de EmailJS para enviar correos automáticos
          a los pacientes (citas creadas, canceladas y reprogramadas).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3072">
          <Input label="Public Key" value={config.publicKey} onChange={e => setConfig({
          ...config,
          publicKey: e.target.value
        })} placeholder="Tu public key de EmailJS" disabled={isReadOnly} data-id="element-3073" />
          
          <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
          ...config,
          serviceId: e.target.value
        })} placeholder="service_xxxxx" disabled={isReadOnly} data-id="element-3074" />
          
          <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
          ...config,
          templateId: e.target.value
        })} placeholder="template_xxxxx" disabled={isReadOnly} data-id="element-3075" />
          
          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: e.target.value
        })} placeholder="Savidna CRM" disabled={isReadOnly} data-id="element-3076" />
          
        </div>

        {saveMessage && <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg text-sm ${saveMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}`} data-id="element-3077">
          
            {saveMessage.type === 'success' ? <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3078" /> : <AlertCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3079" />}
            <span data-id="element-3080">{saveMessage.text}</span>
          </div>}

        {!isReadOnly && <div className="flex justify-end mt-4" data-id="element-3081">
            <Button onClick={handleSave} isLoading={isSaving} data-id="element-3082">
              Guardar configuración
            </Button>
          </div>}

        {isReadOnly && <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 italic" data-id="element-3083">
            Solo lectura — no tienes permisos para modificar esta configuración.
          </p>}
      </CardContent>
    </Card>;
}